import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Countdown from "../components/Countdown";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Check, Sparkles, ArrowRight, BadgeDollarSign, Network, Users, Shield } from "lucide-react";

const LAUNCH = "2027-01-02T00:00:00-03:00";

const plans = [
  {
    id: "plan_associado",
    name: "Associado Ativo",
    price: "R$ 39,90",
    highlight: true,
    tagline: "Ativação da participação",
    features: [
      "Clube de associados com produtos e serviços com descontos",
      "Matriz forçada 5x4 — bonificação do 1º ao 4º nível",
      "Pagamento via PIX com ativação automática",
    ],
    levels: [
      { l: 1, v: "R$ 2,00" }, { l: 2, v: "R$ 4,00" }, { l: 3, v: "R$ 8,00" }, { l: 4, v: "R$ 16,00" },
    ],
  },
];

// Matriz forçada 5x4
const matrixLevels = [
  { lvl: 1, pos: 5, value: 2 }, { lvl: 2, pos: 25, value: 4 }, { lvl: 3, pos: 125, value: 8 }, { lvl: 4, pos: 625, value: 16 },
];
const matrixTotal = matrixLevels.reduce((a, l) => a + l.pos, 0); // 780
const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const EarningsTable = () => (
  <Card className="mt-10 p-3 sm:p-8 overflow-x-auto" data-testid="matrix-earnings-table" data-reveal>
    <table className="w-full text-xs sm:text-base">
      <thead className="text-muted-foreground">
        <tr className="border-b border-border">
          <th className="text-left py-3 font-medium">Nível</th>
          <th className="text-right py-3 font-medium"><span className="sm:hidden">Pessoas</span><span className="hidden sm:inline">Quantidade de pessoas</span></th>
          <th className="text-right py-3 font-medium"><span className="sm:hidden">Por pessoa</span><span className="hidden sm:inline">Valor por pessoa</span></th>
          <th className="text-right py-3 font-medium"><span className="sm:hidden">Total</span><span className="hidden sm:inline">Total do nível</span></th>
        </tr>
      </thead>
      <tbody className="font-mono">
        {matrixLevels.map((l) => (
          <tr key={l.lvl} className="border-b border-border/60" data-testid={`earnings-row-${l.lvl}`}>
            <td className="py-3 pl-2">{l.lvl}</td>
            <td className="py-3 text-right">{l.pos.toLocaleString("pt-BR")}</td>
            <td className="py-3 text-right">{brl(l.value)}</td>
            <td className="py-3 text-right">{brl(l.pos * l.value)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="font-mono font-bold">
        <tr data-testid="earnings-total-row">
          <td className="py-4 font-sans">Total</td>
          <td className="py-4 text-right">{matrixTotal.toLocaleString("pt-BR")}</td>
          <td className="py-4 text-right">{brl(matrixLevels.reduce((a, l) => a + l.value, 0))}</td>
          <td className="py-4 text-right gold-text">{brl(matrixLevels.reduce((a, l) => a + l.pos * l.value, 0))}</td>
        </tr>
      </tfoot>
    </table>
  </Card>
);

const heroStats = [
  { icon: Network, label: "Matriz forçada 5x4" },
  { icon: Users, label: `${matrixTotal.toLocaleString("pt-BR")} posições` },
  { icon: BadgeDollarSign, label: "Bonif. até 4 níveis" },
  { icon: Shield, label: "Segurança total" },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative">
      <Header />

      {/* Video de apresentação */}
      <section
        className="pt-24 sm:pt-28 pb-6 relative"
        data-testid="hero-video-section"
        data-reveal
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative w-full overflow-hidden rounded-2xl gold-border bg-black shadow-2xl"
            style={{ aspectRatio: "16 / 9" }}
          >
            <video
              data-testid="hero-video-player"
              className="absolute inset-0 w-full h-full object-contain"
              src="/media/hero-video-v2.mp4"
              controls
              controlsList="nodownload"
              preload="metadata"
              playsInline
            />
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="pt-6 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[520px] rounded-full blur-3xl opacity-25"
               style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 60%)" }} />
          <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[720px] h-[520px] rounded-full blur-3xl opacity-15"
               style={{ background: "radial-gradient(circle, #AA771C 0%, transparent 60%)" }} />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <Badge className="gold-btn mb-7 inline-flex" data-testid="hero-badge" data-reveal>
            <Sparkles className="w-3 h-3 mr-1" /> Pré-lançamento
          </Badge>

          <h1
            className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight max-w-4xl mx-auto"
            data-reveal data-reveal-delay="1" data-testid="hero-title"
          >
            SEJA MUITO BEM-VINDO À <span className="gold-text">CONECTA MAIS</span>.
          </h1>

          <p
            className="mt-7 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed"
            data-reveal data-reveal-delay="2" data-testid="hero-text"
          >
            Conheça um novo conceito de clube de associados, com acesso a produtos e serviços com descontos e uma
            estrutura de <b className="text-foreground">matriz forçada 5x4</b>. Estamos em pré-lançamento!
            {" "}<b className="text-foreground">Lançamento oficial: 2 de janeiro de 2027.</b> Faça seu cadastro,
            conheça o projeto e ative sua participação com a adesão de R$ 39,90.
          </p>

          <div
            className="mt-10 flex flex-wrap justify-center gap-3"
            data-reveal data-reveal-delay="3"
          >
            <Button size="lg" className="gold-btn hover:opacity-95" onClick={() => navigate("/cadastro")} data-testid="hero-cta-register">
              Criar minha conta <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById("planos")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-plans">
              Ativar por R$ 39,90
            </Button>
          </div>

          <div
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto"
            data-reveal data-reveal-delay="4"
          >
            {heroStats.map((s) => (
              <div
                key={s.label}
                className="tile-hover flex items-center justify-center sm:justify-start gap-2 text-sm text-muted-foreground px-3 py-2 rounded-xl"
              >
                <s.icon className="tile-icon h-4 w-4 gold-text shrink-0" />
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Pre-Market card */}
          <div className="mt-16 max-w-md mx-auto" data-reveal>
            <Card id="pre-market" className="card-hover p-6 gold-border relative overflow-hidden text-left" data-testid="pre-market-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Pré-lançamento</div>
              <div className="mt-1 font-display text-2xl font-bold">Lançamento oficial</div>
              <div className="gold-text font-mono text-lg mt-1" data-testid="landing-launch-date">2 de janeiro de 2027</div>
              <div className="mt-6">
                <Countdown targetIso={LAUNCH} />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="planos" className="py-24 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14" data-reveal>
            <span className="section-eyebrow">Participação</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4">
              Cadastre-se e <span className="gold-text">ative por R$ 39,90</span>
            </h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Cadastre-se e ative sua participação no clube e na matriz com a adesão de R$ 39,90 via PIX.
            </p>
          </div>

          <div className="grid gap-6 max-w-md mx-auto">
            {plans.map((p, idx) => (
              <Card
                key={p.id}
                className={`card-hover p-7 relative flex flex-col text-center md:text-left ${p.highlight ? "gold-border md:scale-[1.03]" : ""}`}
                data-testid={`plan-card-${p.id}`}
                data-reveal
                data-reveal-delay={idx + 1}
              >
                {p.highlight && (
                  <Badge className="gold-btn absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <Sparkles className="w-3 h-3 mr-1" /> RECEBA COMISSÕES
                  </Badge>
                )}
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{p.tagline}</div>
                <div className="mt-2 font-display text-xl font-bold">{p.name}</div>
                <div className="mt-3 flex items-baseline justify-center md:justify-start gap-2">
                  <div className="font-display text-4xl font-extrabold">{p.price}</div>
                  <div className="text-sm text-muted-foreground">/30 dias</div>
                </div>

                {p.levels && (
                  <div className="mt-5 grid grid-cols-2 gap-1.5 text-xs" data-testid={`plan-levels-${p.id}`}>
                    {p.levels.map((lv) => (
                      <div key={lv.l} className="flex items-center justify-between px-2 py-1 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">{lv.l}º nível</span>
                        <span className="font-mono gold-text">{lv.v}</span>
                      </div>
                    ))}
                  </div>
                )}

                <ul className="mt-5 space-y-2.5 text-sm flex-1 text-left">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 gold-text shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`mt-7 w-full ${p.highlight ? "gold-btn hover:opacity-95" : ""}`}
                  variant={p.highlight ? "default" : "outline"}
                  onClick={() => navigate("/cadastro")}
                  data-testid={`plan-select-${p.id}`}
                >
                  Cadastrar e ativar
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Matrix */}
      <section id="matriz" className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14" data-reveal>
            <span className="section-eyebrow">Matriz forçada 5x4</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4">
              Uma rede completa com <span className="gold-text">{matrixTotal.toLocaleString("pt-BR")} posições</span> por participante.
            </h2>
            <p className="mt-4 text-sm text-muted-foreground">
              A matriz possui <b className="text-foreground">5 posições por nível</b>, derramamento automático e
              distribui bonificações em 4 níveis: R$ 2,00, R$ 4,00, R$ 8,00 e R$ 16,00 por ativação.
            </p>
            <Button className="mt-8 gold-btn hover:opacity-95" onClick={() => navigate("/cadastro")} data-testid="matrix-cta">
              Entrar na matriz agora <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <Card className="card-hover p-7 max-w-3xl mx-auto" data-reveal data-reveal-delay="1">
            <div className="space-y-3">
              {matrixLevels.map((l, i) => (
                <div key={l.lvl} className="flex items-center gap-4" data-reveal data-reveal-delay={i + 1}>
                  <div className="w-16 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Nível {l.lvl}</div>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full gold-btn" style={{ width: `${(l.pos / 625) * 100}%` }} />
                  </div>
                  <div className="font-mono text-sm gold-text w-20 text-right">{l.pos.toLocaleString("pt-BR")}</div>
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-border flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Total de posições</div>
                <div className="font-mono font-bold text-lg gold-text">{matrixTotal.toLocaleString("pt-BR")}</div>
              </div>
            </div>
          </Card>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <EarningsTable />
        </div>
      </section>

      <Footer />
    </div>
  );
}
